import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, UserCheck, UserX, Lock, Loader2, LogIn } from 'lucide-react';
import Modal from '../ui/Modal';
import DataGrid from '../grid/DataGrid';
import apiService from '../../services/api';
import { User, Role } from '../../types';
import toast from 'react-hot-toast';
import PhoneInput from '../forms/PhoneInput';
import { useAuth } from '../../contexts/AuthContext';

interface CompanyUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
}

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  roleId: number;
  phone: string;
  city: string;
  state: string;
  zipCode: string;
  sendWelcomeEmail: boolean;
}

const CompanyUsersModal: React.FC<CompanyUsersModalProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName
}) => {
  const { startImpersonation } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<{ temporaryPassword: string; emailSent: boolean } | null>(null);

  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    roleId: 0,
    phone: '',
    city: '',
    state: '',
    zipCode: '',
    sendWelcomeEmail: true
  });

  useEffect(() => {
    if (isOpen && companyId) {
      loadData();
    }
  }, [isOpen, companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse] = await Promise.all([
        apiService.getSuperAdminCompanyUsers(companyId),
        apiService.getCompanyRoles(companyId)
      ]);

      if (usersResponse.success) {
        setUsers(usersResponse.data || []);
      }

      if (rolesResponse.success) {
        setRoles(rolesResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading users data:', error);
      toast.error('Failed to load users data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.roleId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setActionLoading('create');
      const response = await apiService.createCompanyUser(companyId, {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        roleId: formData.roleId,
        sendWelcomeEmail: formData.sendWelcomeEmail,
        phone: formData.phone || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined
      });

      if (response.success) {
        toast.success('User created successfully');
        setShowCreateForm(false);
        resetForm();
        await loadData();
      } else {
        toast.error(response.message || 'Failed to create user');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser || !formData.email || !formData.firstName || !formData.lastName || !formData.roleId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setActionLoading('update');
      const response = await apiService.updateCompanyUser(companyId, selectedUser.id, {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        roleId: formData.roleId,
        isActive: selectedUser.isActive,
        phone: formData.phone || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined
      });

      if (response.success) {
        toast.success('User updated successfully');
        setShowEditForm(false);
        setSelectedUser(null);
        resetForm();
        await loadData();
      } else {
        toast.error(response.message || 'Failed to update user');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading('delete');
      const response = await apiService.deleteCompanyUser(companyId, selectedUser.id);

      if (response.success) {
        toast.success('User deleted successfully');
        setShowDeleteConfirm(false);
        setSelectedUser(null);
        await loadData();
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading('reset-password');
      const response = await apiService.resetUserPassword(companyId, selectedUser.id);

      if (response.success) {
        setResetPasswordResult(response.data);
        toast.success('Password reset successfully');
        setShowResetPasswordConfirm(false);
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      setActionLoading(`toggle-${user.id}`);
      const response = await apiService.toggleUserStatus(companyId, user.id);

      if (response.success) {
        toast.success(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
        await loadData();
      } else {
        toast.error(response.message || 'Failed to toggle user status');
      }
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.message || 'Failed to toggle user status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (user: User) => {
    try {
      setActionLoading(`impersonate-${user.id}`);
      const response = await apiService.impersonateUser(companyId, user.id);

      if (response.success && response.data) {
        const { token, impersonatedUserName, impersonatedUserEmail } = response.data;
        const impersonatedUserData: User = {
          ...user,
          firstName: user.firstName,
          lastName: user.lastName,
          email: impersonatedUserEmail
        };
        
        startImpersonation(token, impersonatedUserData);
        toast.success(`Now impersonating ${impersonatedUserName}`);
        onClose(); // Close the modal
        // Redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        toast.error(response.message || 'Failed to start impersonation');
      }
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      toast.error(error.response?.data?.message || 'Failed to start impersonation');
    } finally {
      setActionLoading(null);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      roleId: 0,
      phone: '',
      city: '',
      state: '',
      zipCode: '',
      sendWelcomeEmail: true
    });
  };

  const openCreateForm = () => {
    resetForm();
    setShowCreateForm(true);
    setShowEditForm(false);
  };

  const openEditForm = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      roleId: user.roleId,
      phone: user.phone || '',
      city: user.city || '',
      state: user.state || '',
      zipCode: user.zipCode || '',
      sendWelcomeEmail: false
    });
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const userColumns = [
    {
      id: 'name',
      header: 'Name',
      cell: (_value: any, item: User) => (
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {item.firstName} {item.lastName}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{item.email}</div>
        </div>
      )
    },
    {
      id: 'role',
      header: 'Role',
      cell: (_value: any, item: User) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">{item.roleName || 'N/A'}</span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      cell: (_value: any, item: User) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          item.isActive
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {item.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'createdDate',
      header: 'Created',
      cell: (_value: any, item: User) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatDate(item.createdDate)}
            </span>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openEditForm(item);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit User"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleStatus(item);
              }}
              disabled={actionLoading === `toggle-${item.id}`}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              title={item.isActive ? 'Deactivate' : 'Activate'}
            >
              {actionLoading === `toggle-${item.id}` ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : item.isActive ? (
                <UserX className="h-4 w-4" />
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleImpersonate(item);
              }}
              disabled={actionLoading === `impersonate-${item.id}` || !item.isActive}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Login As User"
            >
              {actionLoading === `impersonate-${item.id}` ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(item);
                setShowResetPasswordConfirm(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Reset Password"
            >
              <Lock className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(item);
                setShowDeleteConfirm(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete User"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Manage Users - ${companyName}`} maxWidth="5xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Manage Users - ${companyName}`} maxWidth="5xl">
        <div className="space-y-6">
          {/* Header with Create Button */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {users.length} user{users.length !== 1 ? 's' : ''} found
            </p>
            <button
              onClick={openCreateForm}
              className="btn-primary flex items-center gap-2 px-4 py-2"
            >
              <Plus className="w-4 h-4" />
              Create User
            </button>
          </div>

          {/* Users Grid */}
          <DataGrid
            data={users}
            columns={userColumns}
          />

          {/* Create User Form */}
          {showCreateForm && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Create New User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="0">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(value) => setFormData({ ...formData, phone: value })}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.sendWelcomeEmail}
                      onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Send welcome email</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={actionLoading === 'create'}
                  className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'create' ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    'Create User'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Edit User Form */}
          {showEditForm && selectedUser && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Edit User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="0">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(value) => setFormData({ ...formData, phone: value })}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowEditForm(false);
                    setSelectedUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={actionLoading === 'update'}
                  className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'update' ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </span>
                  ) : (
                    'Update User'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedUser(null);
          }}
          title="Delete User"
          maxWidth="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This will deactivate the user account. The user will no longer be able to log in.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading === 'delete'}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'delete' ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </span>
                ) : (
                  'Delete User'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetPasswordConfirm && selectedUser && (
        <Modal
          isOpen={showResetPasswordConfirm}
          onClose={() => {
            setShowResetPasswordConfirm(false);
            setSelectedUser(null);
            setResetPasswordResult(null);
          }}
          title="Reset Password"
          maxWidth="sm"
        >
          <div className="space-y-4">
            {!resetPasswordResult ? (
              <>
                <p className="text-gray-600 dark:text-gray-400">
                  Reset password for <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  A new temporary password will be generated and sent to the user's email address.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowResetPasswordConfirm(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={actionLoading === 'reset-password'}
                    className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'reset-password' ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Resetting...
                      </span>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                    Password reset successfully!
                  </p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Temporary Password:</label>
                      <div className="mt-1 p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded font-mono text-sm">
                        {resetPasswordResult.temporaryPassword}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {resetPasswordResult.emailSent
                        ? '✓ Password has been sent to the user\'s email address'
                        : '⚠ Email could not be sent. Please provide the password to the user manually.'}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowResetPasswordConfirm(false);
                      setSelectedUser(null);
                      setResetPasswordResult(null);
                    }}
                    className="btn-primary px-4 py-2"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default CompanyUsersModal;

